/**
 * Arbol de navegacion.
 *
 * Se dibuja con lo que devuelve `GET /api/v1/menus/`: instalar un modulo agrega
 * ramas sin tocar este archivo. El backend ya podo lo que el usuario no puede
 * leer, asi que aca no se decide nada de seguridad.
 *
 * **Las ramas raiz arrancan CERRADAS.** Con dos modulos, abrirlas todas era una
 * comodidad; con doce son 84 enlaces y 2.9 metros de scroll para llegar a
 * «Configuracion», que es el ultimo de los catorce menus raiz. Cerradas, la
 * barra muestra las catorce areas de un vistazo y el usuario elige una. La unica
 * que se abre sola es la que contiene la pantalla actual —incluso si se llego
 * por la paleta de comandos o por un enlace profundo— y lo que el usuario abre a
 * mano se respeta mientras dure la sesion.
 *
 * Lo activo se resuelve por NOMBRE DE MENU, no por modelo. Cuatro entradas
 * («Todos», «Clientes», «Proveedores», «Empleados») apuntan al mismo
 * `base.partner` y se distinguen solo por su filtro: marcar por modelo las
 * encendia a las cuatro. Por el mismo motivo no alcanza con el `isActive` de
 * `NavLink`, que compara la ruta e ignora la query.
 */

import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { ICONOS_DE_MENU, IconChevronRight, IconDot, IconFolder } from '@/components/shell/icons';
import { containsMenu, menuPath } from '@/components/shell/menu-utils';
import type { MenuNode } from '@/lib/types';

function IconoDeMenu({ node, esRama }: { node: MenuNode; esRama: boolean }) {
  const Componente = node.icon !== null ? ICONOS_DE_MENU[node.icon] : undefined;
  if (Componente !== undefined) return <Componente className="h-[1.125rem] w-[1.125rem] shrink-0" />;
  if (esRama) return <IconFolder className="h-[1.125rem] w-[1.125rem] shrink-0" />;
  return <IconDot className="h-[1.125rem] w-[1.125rem] shrink-0 opacity-45" />;
}

interface NavCommon {
  /** `name` del menu por el que se entro a la pantalla actual. */
  activeMenu: string | null;
  /** Se llama al navegar: en movil cierra el cajon. */
  onNavigate: () => void;
  /**
   * Hay un filtro escrito: el arbol llega ya podado y todo se dibuja abierto,
   * porque esconder una coincidencia detras de un desplegable es no encontrarla.
   */
  filtrando?: boolean;
  depth?: number;
}

interface NavProps extends NavCommon {
  nodes: MenuNode[];
}

interface NavItemProps extends NavCommon {
  node: MenuNode;
  depth: number;
}

export function SidebarNav({ nodes, activeMenu, onNavigate, filtrando = false, depth = 0 }: NavProps) {
  return (
    <ul className={clsx('space-y-0.5', depth > 0 && 'ml-3 border-l border-line pl-3')}>
      {nodes.map((node) => (
        <li key={node.name}>
          <NavItem
            node={node}
            activeMenu={activeMenu}
            onNavigate={onNavigate}
            filtrando={filtrando}
            depth={depth}
          />
        </li>
      ))}
    </ul>
  );
}

function NavItem({ node, activeMenu, onNavigate, filtrando = false, depth }: NavItemProps) {
  const esRama = node.children.length > 0;
  const contieneActual = activeMenu !== null && containsMenu(node, activeMenu);
  const [abierto, setAbierto] = useState(contieneActual);

  // Al navegar desde la paleta de comandos o desde un enlace profundo, la rama
  // que contiene el destino tiene que abrirse sola.
  useEffect(() => {
    if (contieneActual) setAbierto(true);
  }, [contieneActual]);

  if (!esRama) {
    const destino = menuPath(node);
    if (destino === null) return null;
    const activo = node.name === activeMenu;
    return (
      <Link
        to={destino}
        onClick={onNavigate}
        aria-current={activo ? 'page' : undefined}
        className={clsx(
          'touch-target flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-colors',
          activo ? 'bg-accent-soft font-medium text-accent' : 'text-fg-muted hover:bg-fg/5 hover:text-fg',
        )}
      >
        <IconoDeMenu node={node} esRama={false} />
        <span className="truncate">{node.label}</span>
      </Link>
    );
  }

  const desplegado = filtrando || abierto;

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setAbierto((valor) => !valor);
        }}
        aria-expanded={desplegado}
        className={clsx(
          'touch-target flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-colors',
          depth === 0 ? 'font-semibold text-fg' : 'text-fg-muted',
          // Una rama cerrada que contiene la pantalla actual no puede quedar sin
          // marca: seria la unica pista de donde esta parado el usuario.
          contieneActual && !desplegado && 'bg-accent-soft/60 text-accent',
          'hover:bg-fg/5 hover:text-fg',
        )}
      >
        <IconoDeMenu node={node} esRama />
        <span className="truncate">{node.label}</span>
        <IconChevronRight
          className={clsx(
            'ml-auto h-4 w-4 shrink-0 text-fg-faint transition-transform',
            desplegado && 'rotate-90',
          )}
        />
      </button>
      {desplegado && (
        <div className="mt-0.5">
          <SidebarNav
            nodes={node.children}
            activeMenu={activeMenu}
            onNavigate={onNavigate}
            filtrando={filtrando}
            depth={depth + 1}
          />
        </div>
      )}
    </>
  );
}
